import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, Play, Clock, BookOpen, Award } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import Countdown from "@/components/Countdown";
import { api, money } from "@/lib/api";
import { track } from "@/lib/pixel";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function CourseDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/courses/${slug}`).then(({ data }) => setC(data)).catch(() => setC(false)); }, [slug]);
  useEffect(() => {
    if (c && c.course_id) {
      track("ViewContent", {
        content_ids: [c.course_id],
        content_name: c.title,
        content_type: "product",
        content_category: c.category || "Course",
        value: c.current_price,
        currency: "INR",
      });
    }
  }, [c]);
  if (c === null) return <PublicLayout><div className="container-x py-32 text-ink-500">Loading...</div></PublicLayout>;
  if (c === false) return <PublicLayout><div className="container-x py-32">Course not found</div></PublicLayout>;

  return (
    <PublicLayout>
      <section className="relative">
        <div className="absolute inset-0 -z-10">
          <img src={c.banner || c.thumbnail} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-cream-100/60 via-cream-100/70 to-cream-100" />
        </div>
        <div className="container-x pt-16 md:pt-24 pb-16">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-6">
              <div className="overline">{c.category} • {c.level}</div>
              <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter leading-[0.95]" data-testid="course-title">{c.title}</h1>
              <p className="text-lg text-ink-700 leading-relaxed max-w-2xl">{c.short_description}</p>
              <div className="flex flex-wrap gap-6 text-sm text-ink-600">
                <span className="flex items-center gap-2"><BookOpen className="w-4 h-4" /> {c.lessons_count} lessons</span>
                <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> {c.duration}</span>
                <span className="flex items-center gap-2"><Award className="w-4 h-4" /> {c.level}</span>
              </div>
            </div>
            <aside className="glass rounded-2xl p-6 space-y-4 h-fit sticky top-24" data-testid="course-purchase-card">
              <img src={c.thumbnail} alt={c.title} className="rounded-xl w-full aspect-video object-cover" />
              <div className="flex items-baseline gap-3">
                <span className="font-display text-3xl font-bold">{money(c.current_price)}</span>
                {c.original_price > c.current_price && <span className="text-ink-500 line-through">{money(c.original_price)}</span>}
                {c.discount_percentage > 0 && <span className="ml-auto px-2 py-1 rounded-md bg-gold text-black text-xs font-semibold">{c.discount_percentage}% OFF</span>}
              </div>
              {c.offer?.name && (
                <div className="glass rounded-xl p-4">
                  <div className="overline mb-2">🔥 {c.offer.name}</div>
                  <div className="overline text-ink-600 mb-2">Offer ends in</div>
                  <Countdown endsAt={c.offer.ends_at} compact />
                </div>
              )}
              <Link to={`/checkout/${c.slug}`} className="btn-neon w-full" data-testid="course-enroll-cta">Enroll now</Link>
              <p className="text-xs text-ink-500 text-center">7-day money-back guarantee • Lifetime access</p>
            </aside>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="container-x py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-14">
          <div>
            <div className="overline mb-3">About the course</div>
            <p className="text-ink-700 leading-relaxed whitespace-pre-line">{c.description}</p>
          </div>

          <div>
            <div className="overline mb-3">What you'll learn</div>
            <div className="grid md:grid-cols-2 gap-4">
              {c.what_you_learn?.map((it, i) => (
                <div key={i} className="flex gap-3 items-start"><Check className="w-5 h-5 text-gold mt-1 shrink-0" /><span className="text-ink-800 text-sm">{it}</span></div>
              ))}
            </div>
          </div>

          <div>
            <div className="overline mb-3">Curriculum</div>
            <Accordion type="multiple" className="space-y-2" data-testid="course-curriculum">
              {c.modules?.map((m) => (
                <AccordionItem key={m.module_id} value={m.module_id} className="glass rounded-xl border-none px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <span className="text-left"><span className="text-gold mr-2">{String(m.order).padStart(2, "0")}</span>{m.title}</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="divide-y divide-ink-900/10">
                      {m.lessons?.map((l) => (
                        <li key={l.lesson_id} className="flex items-center gap-3 py-3 text-sm">
                          <Play className="w-4 h-4 text-ink-500" />
                          <span className="flex-1">{l.title}</span>
                          <span className="text-ink-500 text-xs">{l.duration}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {c.bonuses?.length > 0 && (
            <div>
              <div className="overline mb-3">Bonuses</div>
              <ul className="space-y-2">
                {c.bonuses.map((b, i) => (<li key={i} className="flex gap-3 items-start"><Check className="w-5 h-5 text-gold mt-0.5 shrink-0" /><span className="text-ink-800 text-sm">{b}</span></li>))}
              </ul>
            </div>
          )}

          {c.faqs?.length > 0 && (
            <div>
              <div className="overline mb-3">FAQs</div>
              <Accordion type="single" collapsible className="space-y-2">
                {c.faqs.map((f) => (
                  <AccordionItem key={f.faq_id} value={f.faq_id} className="glass rounded-xl border-none px-4">
                    <AccordionTrigger className="hover:no-underline text-left">{f.question}</AccordionTrigger>
                    <AccordionContent className="text-ink-700">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>

        {c.testimonials?.length > 0 && (
          <aside className="space-y-4">
            <div className="overline">Student stories</div>
            {c.testimonials.map((t) => (
              <div key={t.testimonial_id} className="glass rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <img src={t.profile_image} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  <div><div className="text-sm font-medium">{t.name}</div><div className="text-xs text-ink-500">{t.role}</div></div>
                </div>
                <p className="mt-3 text-ink-700 text-sm">"{t.testimonial}"</p>
              </div>
            ))}
          </aside>
        )}
      </section>
    </PublicLayout>
  );
}
